import React, { useState, useEffect, useRef } from 'react';
import useAuthStore from '../stores/authStore';
import './Profile.css';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const SERVER_ORIGIN = API_BASE.replace(/\/api$/, '');

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
  return `${SERVER_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

// Helper function to handle 401 errors
const handleUnauthorized = async () => {
  const { default: useAuthStoreModule } = await import('../stores/authStore');
  // Clear token silently without redirecting
  const state = useAuthStoreModule.getState();
  state.token = null;
  state.user = null;
};

const Profile = () => {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [activeSection, setActiveSection] = useState('profile');
  const avatarInputRef = useRef(null);

  // Profile form state
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    full_name: '',
    location: '',
    avatar_url: '',
    linkedin_link: '',
    level: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    // Handle hash-based navigation on mount
    const hash = window.location.hash.slice(1); // Remove the '#' character
    if (hash === 'upgrade-level') {
      setActiveSection('upgrade');
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    if (activeSection === 'sessions') {
      fetchSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setProfileData({
          username: data.user.username || '',
          email: data.user.email || '',
          full_name: data.user.full_name || '',
          location: data.user.location || '',
          avatar_url: data.user.avatarUrl || '',
          linkedin_link: data.user.linkedin_link || '',
          level: data.user.level || ''
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to load profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please choose an image file' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Avatar must be less than 5MB' });
      return;
    }

    setUploadingAvatar(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(`${API_BASE}/profile/avatar`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to upload avatar');
      }

      setProfileData((prev) => ({ ...prev, avatar_url: data.avatarUrl }));
      const currentToken = useAuthStore.getState().token;
      useAuthStore.getState().setAuth(
        {
          ...useAuthStore.getState().user,
          avatarUrl: data.avatarUrl,
          avatar_url: data.avatarUrl,
        },
        currentToken
      );
      setMessage({ type: 'success', text: 'Avatar updated successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to upload avatar' });
    } finally {
      setUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${API_BASE}/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          username: profileData.username,
          full_name: profileData.full_name,
          location: profileData.location,
          linkedin_link: profileData.linkedin_link
        })
      });

      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Profile updated successfully' });
        const currentToken = useAuthStore.getState().token;
        useAuthStore.getState().setAuth(
          {
            ...useAuthStore.getState().user,
            ...data.user,
            full_name: data.user.full_name,
            avatarUrl: data.user.avatarUrl || profileData.avatar_url,
          },
          currentToken
        );
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setSaving(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      setSaving(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/profile/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Password changed successfully' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch (error) {
      console.error('Password change error:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/sessions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setSessions(data.sessions);
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to load sessions' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load sessions' });
    } finally {
      setLoadingSessions(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to revoke this session?')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized
      if (response.status === 401) {
        await handleUnauthorized();
        return;
      }

      const data = await response.json();

      if (response.ok) {
        setMessage({ type: 'success', text: 'Session revoked successfully' });
        fetchSessions(); // Refresh the list
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to revoke session' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to revoke session' });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Profile Settings</h1>
        <p>Manage your account information and security</p>
      </div>

      {message.text && (
        <div className={`profile-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="profile-layout">
        {/* Left Sidebar */}
        <div className="profile-sidebar">
          <button
            className={`sidebar-item ${activeSection === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveSection('profile')}
          >
            <span>Profile Information</span>
          </button>
          <button
            className={`sidebar-item ${activeSection === 'password' ? 'active' : ''}`}
            onClick={() => setActiveSection('password')}
          >
            <span>Change Password</span>
          </button>
          <button
            className={`sidebar-item ${activeSection === 'sessions' ? 'active' : ''}`}
            onClick={() => setActiveSection('sessions')}
          >
            <span>Active Sessions</span>
          </button>
          <button
            className={`sidebar-item ${activeSection === 'upgrade' ? 'active' : ''}`}
            onClick={() => setActiveSection('upgrade')}
          >
            <span>Upgrade Level</span>
          </button>
        </div>

        {/* Right Content */}
        <div className="profile-content">
          {/* Profile Information Section */}
          {activeSection === 'profile' && (
            <div className="profile-section">
              <h2>Profile Information</h2>
              <form onSubmit={handleProfileSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Username</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={profileData.username}
                  onChange={handleProfileChange}
                  required
                  minLength={3}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileData.email}
                  onChange={handleProfileChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="full_name">Full Name</label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={profileData.full_name}
                onChange={handleProfileChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                name="location"
                value={profileData.location}
                onChange={handleProfileChange}
                placeholder="e.g., San Francisco, CA"
              />
            </div>

            <div className="form-group">
              <label>Profile photo</label>
              <div className="avatar-upload">
                <div className="avatar-preview">
                  {profileData.avatar_url ? (
                    <img src={resolveMediaUrl(profileData.avatar_url)} alt="Avatar preview" />
                  ) : (
                    <span>{(profileData.full_name || profileData.username || '?').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="avatar-upload-actions">
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handleAvatarUpload}
                    hidden
                  />
                  <button
                    type="button"
                    className="profile-button"
                    onClick={() => avatarInputRef.current?.click()}
                    disabled={uploadingAvatar}
                  >
                    {uploadingAvatar ? 'Uploading...' : 'Upload photo'}
                  </button>
                  <small>JPG, PNG, GIF, or WEBP. Max 5MB.</small>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="linkedin_link">LinkedIn Profile</label>
              <input
                type="url"
                id="linkedin_link"
                name="linkedin_link"
                value={profileData.linkedin_link}
                onChange={handleProfileChange}
                placeholder="https://linkedin.com/in/yourprofile"
              />
            </div>

              <button type="submit" className="profile-button" disabled={saving}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </form>
          </div>
          )}

          {/* Change Password Section */}
          {activeSection === 'password' && (
            <div className="profile-section">
              <h2>Change Password</h2>
              <form onSubmit={handlePasswordSubmit}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                autoComplete="current-password"
              />
            </div>

            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
                autoComplete="new-password"
              />
              <small>Must be at least 8 characters long</small>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

              <button type="submit" className="profile-button" disabled={saving}>
                {saving ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
          )}

          {/* Upgrade Level Section */}
          {activeSection === 'upgrade' && (
            <div className="profile-section">
              <h2>Upgrade Level</h2>
              <div className="upgrade-info">
                <p><strong>Current Level:</strong> {profileData.level || 'Not set'}</p>
                <div className="upgrade-message">
                  <p>We are a startup and have not yet integrated payment processing. If you would like to upgrade your level, please feel free to contact us.</p>
                  <p>We are always happy to assist and will promptly upgrade your account to the level you desire.</p>
                </div>
              </div>
            </div>
          )}

          {/* Active Sessions Section */}
          {activeSection === 'sessions' && (
            <div className="profile-section">
              <h2>Active Sessions</h2>
              <p className="section-description">Manage your active login sessions across different devices</p>
              
              {loadingSessions ? (
                <div className="sessions-loading">Loading sessions...</div>
              ) : sessions.length === 0 ? (
                <div className="no-sessions">No active sessions found</div>
              ) : (
                <div className="sessions-list">
                  {sessions.map((session) => (
                    <div key={session.id} className={`session-card ${session.isCurrent ? 'current-session' : ''}`}>
                      <div className="session-info">
                        <div className="session-device">
                          <strong>{session.device}</strong>
                          {session.isCurrent && <span className="current-badge">Current Browser</span>}
                        </div>
                        <div className="session-details">
                          <div className="session-detail">
                            <span className="detail-label">Created:</span>
                            <span className="detail-value">{formatDate(session.createdAt)}</span>
                          </div>
                          <div className="session-detail">
                            <span className="detail-label">Last Active:</span>
                            <span className="detail-value">{formatDate(session.lastActive)}</span>
                          </div>
                          <div className="session-detail">
                            <span className="detail-label">Expires:</span>
                            <span className="detail-value">{formatDate(session.expiresAt)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        className="revoke-button"
                        onClick={() => handleRevokeSession(session.id)}
                        disabled={session.isCurrent}
                        title={session.isCurrent ? "Cannot revoke current session" : "Revoke this session"}
                      >
                        {session.isCurrent ? 'Current' : 'Revoke'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;
